import React, { useEffect } from "react";
import { getDefaulAudioSource } from "../../audio/routing";
import './RollIOControls.css';
import './IOSelectionDialogCommons.css';
import { useMidiAccessStore } from "../../stores/MidiAccessStore";
import { RydInstrument } from "../../model/instrument";
import { RydTrackInstrumentSelectionDialog } from "./InstrumentSelectionDialog";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faHeadphones,
    faMicrophone,
    faSliders
} from "@fortawesome/free-solid-svg-icons";
import { RydTrack } from "../../model/tracks";
import { RecordArmController } from "../../audio/RecordingController";
import { RydToggleButton } from "../widgets/RydToggleButton";
import { NoteActivityObserver } from "../../audio/NoteActivity";
import {
    Note,
    NoteDuration,
    NoteVelocity
} from "../../model/core";
import { useWorkspaceInstrumentAssignments } from "../../stores/WorkspaceStore";
import { useSetInstrumentWidget } from "../../stores/InspectorPanelStore";
import { mapToSynthSurface } from "../audio/instruments/SynthWidgetHelper";
import { RydInputSource } from "../../audio/input";
import { RydTrackSourceSelectionDialog } from "./SourceSelectionDialog";


export function RollIOControls({
    track,
    setSourceCB,
    source,
    trackInstrument,
    setTrackInstrument,
    insertNoteCB
}: {
    track: RydTrack,
    setSourceCB?: (sink: RydInputSource) => void,
    source: RydInputSource,
    trackInstrument?: RydInstrument,
    setTrackInstrument?: (instrument?: RydInstrument) => void,
    insertNoteCB?: (track: RydTrack, start: number, note: Note, duration: NoteDuration, velocity: NoteVelocity) => void,
}) {

    const recordingController = RecordArmController.getInstance();
    const [isArmedForRecord, setIsArmedForRecord] = React.useState(recordingController.isTrackArmed(track.id));
    const [isMonitoring, setIsMonitoring] = React.useState(recordingController.isTrackMonitoring(track.id));
    const workspaceInstrumentAssignments = useWorkspaceInstrumentAssignments();

    const midiAccessStore = useMidiAccessStore();
    const setInstrumentWidget = useSetInstrumentWidget();

    useEffect(() => {
        if (source?.id == getDefaulAudioSource().id) return;
        const armed = recordingController.isTrackArmed(track.id);
        const monitoring = recordingController.isTrackMonitoring(track.id);

        if (!armed && !monitoring) return;
        NoteActivityObserver.getInstance().assignSourceToTrack({ midiAccess: midiAccessStore.midiAccess, sourceId: source.id }, track.id);
        return () => {
            NoteActivityObserver.getInstance().unassignSourceFromTrack({ midiAccess: midiAccessStore.midiAccess, sourceId: source.id }, track.id);
        }
    }, [source, isArmedForRecord, isMonitoring]);

    useEffect(() => {
        const instrument = workspaceInstrumentAssignments.get(track.id);
        if (!instrument) return;

        NoteActivityObserver.getInstance().listen(track.id, ({ event, note, velocity }) => {
            if (event === 'noteon') {
                instrument.noteOn(note, velocity!);
            } else if (event === 'noteoff') {
                instrument.noteOff(note);
            }
        });
        return () => {
            NoteActivityObserver.getInstance().drop(track.id);
        }
    }, [workspaceInstrumentAssignments.get(track.id)]);

    const armForRecord = () => {
        recordingController.armTrack(track.id);
        NoteActivityObserver.getInstance().listenForCompleteNote(track.id, (data: { start: number, note: Note, duration: NoteDuration, velocity: NoteVelocity }) => {
            // we assume that if there's no recording tick, we are not recording.
            if (recordingController.getCurrentRecordingTick() == null) return;
            // write to hnc
            insertNoteCB?.(track, data.start, data.note, data.duration, data.velocity);
        });
        setIsArmedForRecord(true);
    };

    const disarmForRecord = () => {
        recordingController.disarmTrack(track.id);
        NoteActivityObserver.getInstance().dropNoteCompleteListener(track.id);
        setIsArmedForRecord(false);
    };

    const armForMonitoring = () => {
        recordingController.monitorTrack(track.id);
        setIsMonitoring(true);
    };

    const disarmForMonitoring = () => {
        recordingController.unmonitorTrack(track.id);
        setIsMonitoring(false);
    };

    return (<>

            <RydToggleButton
                onActivate={armForMonitoring}
                onDeactivate={disarmForMonitoring}
                active={isMonitoring}
                activeStyle={{
                    backgroundColor: 'lightgreen',
                    color: 'black'
                }}
                inactiveStyle={{
                    backgroundColor: 'lightgrey',
                    color: 'black'
                }}
            >
                <FontAwesomeIcon icon={faHeadphones} />
            </RydToggleButton>

            <RydToggleButton
                onActivate={armForRecord}
                onDeactivate={disarmForRecord}
                active={isArmedForRecord}
                // disabled={!hasMidiAccess || source?.id == getDefaulAudioSource().id}
                activeStyle={{
                    color: 'black',
                    backgroundColor: 'red'
                }}
                inactiveStyle={{
                    color: 'black',
                    backgroundColor: 'lightgrey'
                }}
            >
                <FontAwesomeIcon icon={faMicrophone} aria-description="arm for record" />
            </RydToggleButton>
            <RydTrackSourceSelectionDialog
                source={source}
                setSourceCB={setSourceCB}
            />


        <br />

            <RydTrackInstrumentSelectionDialog
                trackInstrument={trackInstrument}
                setTrackInstrument={setTrackInstrument} />
            <button
                disabled={trackInstrument?.getOutputType() !== 'VSYNTH'}
                onClick={() => {
                    if (!trackInstrument) return;
                    console.log('show synth panel');
                    setInstrumentWidget(mapToSynthSurface(trackInstrument));
                }}
            >
                <FontAwesomeIcon icon={faSliders} />
            </button>

    </>);
}
